import ReminderEditorDialog from '@common/components/reminders/ReminderEditorDialog';
import RemindersList from '@common/components/reminders/RemindersList';
import { useAppDispatch } from '@common/reducers/hooks/store.hook';
import {
    openEditor
} from '@common/reducers/RemindersUIReducer';
import { Box, Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { MessageSquareText, User } from 'lucide-react';
import { TestReminderDialog } from './TestReminderDialog';

// Mock WhatsApp Message HTML as valid JSX for display
const MockMessage = ({ onAddReminder }: { onAddReminder: (text: string, id: string) => void }) => {
    const messageId = "false_120363420104669521@g.us_AC689E7BF4675AB35D5560B2B740B2A0_243752329289849@lid";
    const messageText = "This morning, after 11.30 posting.. Everything stopped";

    return (
        <Paper elevation={3} sx={{ p: 2, bgcolor: 'white', mb: 2, maxWidth: 600, borderLeft: '4px solid #25D366' }}>
             <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>Mock WhatsApp Message</Typography>
             
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    <div style={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', backgroundColor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                        <MessageSquareText size={20} />
                    </div>
                    <Box>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                            {messageText}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            John Doe • 10:30 AM
                        </Typography>
                    </Box>
                </Box>
                <Button 
                    size="small" 
                    variant="contained" 
                    color="secondary"
                    onClick={() => onAddReminder(messageText, messageId)}
                    sx={{ minWidth: 30, p: 0.5, borderRadius: '50%', ml: 2 }}
                    title="Set reminder for this message"
                >
                    🔔
                </Button>
             </Box>
        </Paper>
    );
}; 

// Mock WhatsApp Chat (Contact/Group Header style)
const MockChat = ({ onAddReminder }: { onAddReminder: (chatName: string, id: string) => void }) => {
    const chatId = "972545934111@c.us";
    const chatName = "John Doe (Client)";

    return (
        <Paper elevation={3} sx={{ p: 2, bgcolor: 'white', mb: 2, maxWidth: 600, borderLeft: '4px solid #25D366' }}>
             <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>Mock WhatsApp Chat Context</Typography>
             
             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                        <User size={20} />
                    </div>
                    <Typography variant="h6" sx={{ fontSize: '1rem' }}>{chatName}</Typography>
                </Box>
                <Button 
                    size="small" 
                    variant="contained" 
                    color="primary"
                    onClick={() => onAddReminder(chatName, chatId)}
                    sx={{ minWidth: 30, p: 0.5, borderRadius: '50%' }}
                    title="Set reminder for this chat"
                >
                    🔔
                </Button>
             </Box>
        </Paper>
    );
};

export const TestReminderPage = () => {
    const dispatch = useAppDispatch();

    const handleCreateNew = () => {
        dispatch(openEditor({ reminder: undefined, context: {} }));
    };

    const handleAddFromMessage = (text: string, id: string) => {
        dispatch(openEditor({ 
            reminder: undefined,
            context: { 
                messageText: text, 
                messageId: id,
                chatDisplayName: "Bussines Group", 
                chatId: "false_120363420104669521@g.us"
            }
        }));
    };

    const handleAddFromChat = (chatName: string, chatId: string) => {
        dispatch(openEditor({ 
            reminder: undefined,
            context: { 
                chatDisplayName: chatName,
                chatId: chatId 
            }
        }));
    };
    
    return (
        <Box sx={{ p: 4, height: '100vh', overflow: 'auto', bgcolor: '#f0f2f5' }}>
            <Typography variant="h4" gutterBottom>Reminder Feature Test</Typography>
            <Divider sx={{ mb: 4 }} />

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Mock WhatsApp Chat</Typography>
                    <MockMessage onAddReminder={handleAddFromMessage} />
                    <MockChat onAddReminder={handleAddFromChat} />
                    
                    <Box sx={{ mt: 4 }}>
                         <Button variant="contained" onClick={handleCreateNew}>
                             Create Independent Reminder
                         </Button>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Side Panel (Reminders List)</Typography>
                    <Paper sx={{ minHeight: 400, bgcolor: 'white' }}>
                         <RemindersList />
                    </Paper>
                </Grid>
            </Grid>

            {/* Editor Dialog */}
            <ReminderEditorDialog />

            {/* Test Reminder Dialog */}
            <TestReminderDialog />
        </Box>
    );
};

export default TestReminderPage;
